import React from "react";
import Tooltip from "@mui/material/Tooltip";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import UnsubscribeOutlinedIcon from "@mui/icons-material/UnsubscribeOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import MetricCardBlock from "../today/blocks/MetricCardBlock";
import MetricTableBlock from "../today/blocks/MetricTableBlock";

// Render the configured structure without fetching store data or enabling entries.
export default function ConfigurationBlockPreview({
  block,
  label,
  category,
  sources,
  actions,
  onEdit,
}) {
  const recipients =
    block.type === "category"
      ? category?.notify_user_ids
      : ["metric", "table"].includes(block.type)
        ? block.config?.notify_user_ids
        : [];
  const recipientCount = new Set((recipients || []).map(String)).size;
  const notificationLabel = `Notifies ${recipientCount} ${recipientCount === 1 ? "person" : "people"} ${block.type === "category" ? "on submission" : "when flagged"}`;
  const headerActions = (
    <div className="flex items-center gap-1 shrink-0">
      {recipientCount > 0 && (
        <Tooltip title={notificationLabel} arrow>
          <span
            role="img"
            aria-label={notificationLabel}
            tabIndex={0}
            className="inline-flex rounded p-1 text-violet-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          >
            <NotificationsActiveOutlinedIcon style={{ fontSize: 17 }} />
          </span>
        </Tooltip>
      )}
      {block.include_in_email === false && (
        <Tooltip title="Not included in the daily email" arrow>
          <span
            role="img"
            aria-label="Not included in the daily email"
            tabIndex={0}
            className="inline-flex rounded p-1 text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          >
            <UnsubscribeOutlinedIcon style={{ fontSize: 18 }} />
          </span>
        </Tooltip>
      )}
      {actions}
    </div>
  );
  if (block.type === "section") {
    return (
      <div className="flex items-center justify-between gap-2 w-full pt-2">
        <button
          onClick={onEdit}
          className="text-left text-sm font-bold uppercase tracking-wider text-slate-500 rounded focus-visible:ring-2 focus-visible:ring-violet-500"
        >
          {label}
        </button>
        {headerActions}
      </div>
    );
  }
  const sourceFor = (key) => sources.find((source) => source.key === key);
  if (block.type === "metric") {
    return (
      <MetricCardBlock
        block={block}
        metric={{
          label,
          value: null,
          format: sourceFor(block.config?.source)?.format || "number",
          threshold: null,
          vs_last_week: null,
          series: [],
        }}
        headerActions={headerActions}
        onTitleClick={onEdit}
        canAddEntries={false}
      />
    );
  }
  if (block.type === "table") {
    return (
      <MetricTableBlock
        block={block}
        table={{
          label,
          columns: block.config?.columns,
          rows: (block.config?.rows || []).map((row) => ({
            label: row.label || sourceFor(row.source)?.label || row.source,
            value: null,
            format: sourceFor(row.source)?.format || "number",
            threshold: null,
            vs_last_week: null,
          })),
        }}
        headerActions={headerActions}
        onTitleClick={onEdit}
        canAddEntries={false}
      />
    );
  }
  if (block.type === "agent") {
    return (
      <div
        className="w-full border border-violet-200 rounded-xl px-4 py-3.5"
        style={{
          background: "linear-gradient(180deg, #f8f6ff 0%, #ffffff 70%)",
        }}
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-violet-700 flex items-center justify-center shrink-0">
            <AutoAwesomeOutlinedIcon style={{ fontSize: 13, color: "white" }} />
          </div>
          <button
            onClick={onEdit}
            className="flex-1 text-left text-sm font-semibold text-slate-900 rounded focus-visible:ring-2 focus-visible:ring-violet-500"
          >
            {label}
          </button>
          {headerActions}
        </div>
        <p className="mt-2 text-sm text-slate-500">
          The daily summary will appear here.
        </p>
      </div>
    );
  }
  return (
    <div className="w-full bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-3.5">
        <button
          onClick={onEdit}
          className="flex flex-1 min-w-0 items-center gap-3 text-left rounded focus-visible:ring-2 focus-visible:ring-violet-500"
          aria-label={label}
        >
          <span className="w-8 h-8 shrink-0 rounded-lg bg-slate-100 flex items-center justify-center">
            <KeyboardArrowRightIcon
              style={{ fontSize: 18, color: "#64748b" }}
            />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-900 break-words">
              {label}
            </span>
            {category?.description && (
              <span className="block text-xs text-slate-500 mt-0.5 break-words">
                {category.description}
              </span>
            )}
          </span>
        </button>
        <span className="px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-xs text-slate-500 whitespace-nowrap">
          0 entries
        </span>
        {headerActions}
      </div>
    </div>
  );
}
